// src/lib/today/daySentence.ts
//
// Today's header sentence (spec 2026-10-14, "Today, trimmed", frame After 1):
// the day said back in one or two short sentences, from fixed rules over the
// day's own data. Nothing here is generated by the model (wall philosophy
// rule 5), and an empty day says nothing.
//
//   "CEPI standby until 10, then 7¼ hours open. Return the slips at 7 tonight."
//   "2 new from email."
//
// Pure: the page hands in the day's timed things and the clock.

import { freeHoursLabel, DEFAULT_END } from './dayScale'

export interface DaySentenceItem {
  title: string
  start: Date
  /** No end: half an hour, as everywhere else on Today. */
  end?: Date | null
  /** Routines shape the open stretches but are never named: "School morning
   *  until 8" says nothing the day doesn't already know. */
  kind: 'event' | 'task' | 'routine'
  completed?: boolean
}

export interface DaySentenceInput {
  items: readonly DaySentenceItem[]
  /** The clock on today; null when looking at another day. */
  now: Date | null
  /** Unreviewed email captures. */
  emailCount?: number
  /** Only describe open time after the connected calendars finish loading. */
  calendarReady?: boolean
}

export interface DaySentence {
  /** What the day holds, or null when it holds nothing with a time. */
  day: string | null
  /** "2 new from email." — the page links it. */
  email: string | null
}

const HALF_HOUR = 30 * 60_000
/** An hour or more counts as open, as on the day scale. */
const MIN_OPEN_MS = 60 * 60_000
/** From here on, a thing is "tonight". */
const EVENING_HOUR = 17

/** "10", "10:30" — the way a person says it; the sentence says which half. */
export function sayClock(d: Date): string {
  const h = d.getHours() % 12 || 12
  return d.getMinutes() ? `${h}:${String(d.getMinutes()).padStart(2, '0')}` : `${h}`
}

const endOf = (i: DaySentenceItem) =>
  i.end && i.end.getTime() > i.start.getTime() ? i.end : new Date(i.start.getTime() + HALF_HOUR)

/** The open stretch starting at `from`, if the next taken time (or the end of
 *  the day's column) is an hour or more away. */
function openAfter(from: Date, busy: readonly { start: Date; end: Date }[]): { label: string; until: Date | null } | null {
  let cursor = from.getTime()
  // Anything already running at `from` pushes the stretch back.
  for (const b of busy) {
    if (b.start.getTime() <= cursor && b.end.getTime() > cursor) cursor = b.end.getTime()
  }
  const next = busy.filter((b) => b.start.getTime() >= cursor).sort((a, b) => a.start.getTime() - b.start.getTime())[0]
  const dayEnd = new Date(from); dayEnd.setHours(DEFAULT_END, 0, 0, 0)
  const until = next ? next.start.getTime() : Math.max(dayEnd.getTime(), cursor)
  if (until - cursor < MIN_OPEN_MS) return null
  if (!next) return { label: 'the rest of the day is open', until: null }
  return { label: freeHoursLabel((until - cursor) / 3_600_000), until: next.start }
}

const cap = (s: string) => `${s[0].toUpperCase()}${s.slice(1)}`

function dayPart(items: readonly DaySentenceItem[], now: Date | null): string | null {
  const live = items.filter((i) => !i.completed)
  if (!live.length) return null
  const nowMs = now?.getTime() ?? null
  const all = live.map((i) => ({ ...i, end: endOf(i) }))
  const remaining = nowMs === null ? all : all.filter((i) => i.end.getTime() > nowMs)
  if (!remaining.length) return 'The rest of the day is open.'
  const busy = remaining.map((i) => ({ start: i.start, end: i.end }))
  const named = remaining.filter((i) => i.kind !== 'routine').sort((a, b) => a.start.getTime() - b.start.getTime())

  const parts: string[] = []
  const said = new Set<DaySentenceItem>()
  const current = nowMs === null ? undefined : named.find((i) => i.start.getTime() <= nowMs && i.end.getTime() > nowMs)
  if (current) {
    said.add(current)
    const open = openAfter(current.end, busy)
    parts.push(`${current.title} until ${sayClock(current.end)}${open ? `, then ${open.label}` : ''}.`)
  } else {
    const next = named.find((i) => nowMs === null || i.start.getTime() >= nowMs)
    const openNow = now ? openAfter(now, busy) : null
    if (next) {
      said.add(next)
      // The room before it, when nothing else (a routine) takes it first.
      if (openNow?.until && openNow.until.getTime() === next.start.getTime()) {
        parts.push(`${cap(openNow.label)}, then ${next.title} at ${sayClock(next.start)}.`)
      } else {
        const after = openAfter(next.end, busy)
        parts.push(`${next.title} at ${sayClock(next.start)}${after ? `, then ${after.label}` : ''}.`)
      }
    } else if (openNow) {
      // Only routines left: say the room, not the routine.
      parts.push(`${cap(openNow.label)}.`)
    }
  }

  // The last fixed thing tonight, when it is not the one already named.
  const tonight = named.filter((i) => i.start.getHours() >= EVENING_HOUR && !said.has(i)).at(-1)
  if (tonight) parts.push(`${tonight.title} at ${sayClock(tonight.start)} tonight.`)
  return parts.length ? parts.join(' ') : null
}

export function daySentence({ items, now, emailCount = 0, calendarReady = true }: DaySentenceInput): DaySentence {
  const recorded = items.filter(i => !i.completed && i.kind !== 'routine' && (!now || endOf(i) > now))
    .sort((a, b) => a.start.getTime() - b.start.getTime()).slice(0, 2)
  return {
    day: calendarReady ? dayPart(items, now) : recorded.length
      ? `Recorded: ${recorded.map(i => `${i.title} at ${sayClock(i.start)}`).join('; ')}.` : null,
    email: emailCount > 0 ? `${emailCount} new from email.` : null,
  }
}
